import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Spinner, Badge, Button, Alert, Form, Dropdown } from "react-bootstrap";
import {
  FaCheckCircle, FaExclamationTriangle, FaChevronLeft, FaTrash,
  FaFilePdf, FaFileInvoiceDollar, FaUndo, FaBolt, FaDownload, FaFileAlt,
} from "react-icons/fa";
import { downloadQuoteReport, REPORT_STYLES } from "./utils/quoteReports";
import sessionStorage from "./utils/sessionStore";
import { apiFetch, hasAction } from "./utils/auth";
import { isReviewApprover, reviewStatusMeta, statusColor, systemCheckBg } from "./utils/reviewAccess";
import AckStatusPanel from "./AckStatusPanel";
import "./pagecss/Webappcss.css";
import "./pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

function fmtMoney(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}
function fmtWhen(v) {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

const NON_TERMINAL = ["PendingReview", "Approved", "Creating"];

// =============================================================================
// QuoteReviewDetail — the CS tally + decision screen for one quote.
//   Left:  the review snapshot + coverage tally + every uploaded PO (view/delete).
//   Below: Approve / Create-now / Return (CS only) + the held-PO guard.
//   Foot:  the append-only reviewer chain (who did what, when).
// =============================================================================
function QuoteReviewDetail() {
  const { quoteNum } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Where "Back to Queue" returns to. The queue passes its exact origin (tab +
  // focus=this quote) via router state so we land back on the same bucket with
  // this quote re-located/highlighted; falls back to the queue default when the
  // detail was opened directly (deep link).
  const backToQueue = location.state?.from || "/reviews";
  const approver = isReviewApprover();

  const [review, setReview]   = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [pos, setPos]         = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // action state
  const [busy, setBusy]           = useState(null);   // "approve" | "createNow" | "return" | `del-${id}`
  const [actionErr, setActionErr] = useState(null);
  // Quote report (PDF) download — same helper the edit-quote page uses.
  const [downloading, setDownloading] = useState(false);
  const [downloadErr, setDownloadErr] = useState(null);
  const [ackHeld, setAckHeld]     = useState(false);
  const [returning, setReturning] = useState(false);  // return-reason form open
  const [reason, setReason]       = useState("");

  useEffect(() => {
    if (!sessionStorage.getItem("authToken")) navigate("/login");
  }, [navigate]);

  const loadAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [rv, cov, att] = await Promise.all([
        apiFetch(`${API_URL}/api/QuoteReview/${encodeURIComponent(quoteNum)}`).then((r) => r.json().catch(() => null)),
        apiFetch(`${API_URL}/api/Quote/${encodeURIComponent(quoteNum)}/coverage`).then((r) => r.json().catch(() => null)),
        apiFetch(`${API_URL}/api/Attachment?quoteNum=${encodeURIComponent(quoteNum)}`).then((r) => r.json().catch(() => [])),
      ]);
      if (!rv || rv.error) throw new Error(rv?.error || "No review record for this quote.");
      setReview(rv);
      setCoverage(cov && !cov.error ? cov : null);
      setPos(Array.isArray(att) ? att : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [quoteNum]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Live-refresh while the worker is creating the order.
  useEffect(() => {
    if (!review || !NON_TERMINAL.includes(review.status) || review.status === "PendingReview") return;
    const id = setInterval(loadAll, 6000);
    return () => clearInterval(id);
  }, [review, loadAll]);

  const heldCount = pos.filter((p) => p.confirmedPOAmount == null).length;

  // ── Actions ────────────────────────────────────────────────────────────────
  const runAction = async (key, url, opts) => {
    setBusy(key); setActionErr(null);
    try {
      const r = await apiFetch(url, { method: "POST", ...(opts || {}) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        // create-now failure still returns the updated review under .review
        if (data?.review) setReview(data.review);
        setActionErr({ code: data?.errorCode, message: data?.error || data?.message || `HTTP ${r.status}` });
        return false;
      }
      setReview(data?.review || data);
      setReturning(false); setReason(""); setAckHeld(false);
      await loadAll();
      return true;
    } catch (e) {
      setActionErr({ message: e.message });
      return false;
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = async (variant) => {
    setDownloading(true); setDownloadErr(null);
    try {
      await downloadQuoteReport(quoteNum, variant);
    } catch (e) {
      setDownloadErr(e.message);
    } finally {
      setDownloading(false);
    }
  };

  // ── PO document downloads (mirrors the edit-quote page) ──────────────────────
  const [poDownloading, setPoDownloading] = useState(false);

  const poLabel = (p) => {
    const po = p.confirmedPONumber || p.extractedPONumber;
    return po ? `${p.fileName || `PO ${p.id}`} · PO ${po}` : (p.fileName || `PO ${p.id}`);
  };

  // Resolve one attachment's short-lived URL and trigger a save.
  const downloadOnePO = async (p) => {
    const r = await apiFetch(`${API_URL}/api/Attachment/${p.id}/download-url`);
    const d = await r.json().catch(() => null);
    const url = d?.downloadUrl || d?.url || d?.sasUrl;
    if (!url) throw new Error(`No download link for ${p.fileName || `PO ${p.id}`}.`);
    const a = document.createElement("a");
    a.href = url; a.target = "_blank"; a.rel = "noopener"; a.download = p.fileName || `PO_${p.id}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  // Download every PO as ONE .zip (server-built) — the browser blocks firing N
  // separate cross-origin saves, so a single archive is the only reliable path.
  const handleDownloadAllPOs = async () => {
    setPoDownloading(true); setDownloadErr(null);
    try {
      const r = await apiFetch(`${API_URL}/api/Attachment/quote/${encodeURIComponent(quoteNum)}/po-zip`);
      if (!r.ok) {
        let msg = `HTTP ${r.status}`;
        try { const d = await r.json(); msg = d.error || d.message || msg; } catch { /* non-JSON */ }
        setDownloadErr(msg);
        return;
      }
      const blob = await r.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl; a.download = `Quote_${quoteNum}_POs.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (e) {
      setDownloadErr(`PO download failed: ${e.message}`);
    } finally {
      setPoDownloading(false);
    }
  };

  const approve   = () => runAction("approve",   `${API_URL}/api/QuoteReview/${encodeURIComponent(quoteNum)}/approve?acknowledgeHeld=${ackHeld}`);
  const createNow = () => runAction("createNow", `${API_URL}/api/QuoteReview/${encodeURIComponent(quoteNum)}/create-now?acknowledgeHeld=${ackHeld}`);
  const submitReturn = () => {
    if (!reason.trim()) { setActionErr({ message: "A reason is required to return the quote." }); return; }
    runAction("return", `${API_URL}/api/QuoteReview/${encodeURIComponent(quoteNum)}/return`, {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason: reason.trim() }),
    });
  };

  const viewPO = async (id) => {
    try {
      const r = await apiFetch(`${API_URL}/api/Attachment/${id}/download-url`);
      const data = await r.json().catch(() => null);
      const url = data?.downloadUrl || data?.url || data?.sasUrl;
      if (url) window.open(url, "_blank", "noopener");
      else setActionErr({ message: "Could not get a download link for that PO." });
    } catch (e) { setActionErr({ message: e.message }); }
  };

  const deletePO = async (id) => {
    setBusy(`del-${id}`); setActionErr(null);
    try {
      const r = await apiFetch(`${API_URL}/api/Attachment/${id}`, { method: "DELETE" });
      if (!r.ok && r.status !== 204) throw new Error(`Delete failed (HTTP ${r.status}).`);
      await loadAll();
    } catch (e) { setActionErr({ message: e.message }); }
    finally { setBusy(null); }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  const meta = review ? reviewStatusMeta(review.status, review.termsCode) : null;
  const canDecide = approver && review?.status === "PendingReview";

  // Live system check derived from the CURRENT coverage + held count, so deleting
  // a PO updates the badge immediately instead of showing the stale snapshot from
  // submit time. Falls back to the stored value before coverage loads. (G5)
  const liveSystemCheck = review && (review.status === "PendingReview" || review.status === "CreationFailed") && coverage
    ? (!coverage.isMinimumMet ? "NotReady" : heldCount > 0 ? "Hold" : "Ready")
    : review?.systemCheck;
  // Multi-ship (terms M?K): CS approves the PLAN once, before any PO; each delivery then
  // converts from the multi-ship page as its PO arrives — so whole-quote coverage does not
  // block Approve, and there is no Create now (SAL-GAP-64).
  const multiShip = /^M[0-9A-Z]K$/i.test((review?.termsCode || "").trim());
  const coverageBlocks = !multiShip && liveSystemCheck === "NotReady";
  // Decision 7A (2026-10-31 gap 10): an order-creation attempt failed (or the POs changed after
  // it failed) — the blue note tells CS to run Create now, so offer ONLY Create now here (no
  // Approve / Return). The server still refuses the quote's own submitter (SELF_APPROVAL) and
  // re-checks coverage + held POs.
  const canRetryCreate = approver && review?.status === "CreationFailed" && !multiShip;

  return (
    <div className="qw-root content-halo" style={{ padding: "28px 24px 36px", maxWidth: "min(1080px, 95vw)", margin: "84px auto 56px", borderRadius: 16, minHeight: "calc(100vh - 140px)" }}>
      <div className="qw-header" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="qw-header-icon"><FaFileInvoiceDollar size={18} /></div>
          <div>
            <div className="qw-header-title">Review · Quote #{quoteNum}</div>
            <div className="qw-header-sub">Tally the POs, then approve, create, or return</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {/* Download the quote report (PDF) — same 3 report styles the edit-quote
              page offers, via the shared downloadQuoteReport helper. */}
          <Dropdown>
            <Dropdown.Toggle
              className="qw-btn qw-btn-sm"
              disabled={downloading}
              style={{ background: "rgba(255,255,255,.18)", color: "#fff", border: "none", fontWeight: 600 }}
            >
              <FaDownload style={{ marginRight: 6 }} /> {downloading ? "Preparing…" : "Download Quote"}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {REPORT_STYLES.map((s) => (
                <Dropdown.Item key={s.value} onClick={() => handleDownload(s.value)}>{s.label}</Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          {/* Download the customer PO document(s) attached to this quote — the
              same control the edit-quote page offers. Single .zip for all, or
              pick one; the list scrolls (vert + horiz) for long filenames. */}
          <Dropdown align="end">
            <Dropdown.Toggle
              className="qw-btn qw-btn-sm"
              disabled={poDownloading}
              style={{ background: "rgba(255,255,255,.18)", color: "#fff", border: "none", fontWeight: 600 }}
            >
              <FaFileAlt style={{ marginRight: 6 }} /> {poDownloading ? "Preparing…" : "Download PO"}
            </Dropdown.Toggle>
            <Dropdown.Menu className="po-menu" style={{ minWidth: 240, maxWidth: "min(92vw, 380px)" }}>
              {pos.length > 0 ? (
                <>
                  <Dropdown.Item onClick={handleDownloadAllPOs} style={{ fontWeight: 600 }}>
                    <FaDownload style={{ marginRight: 6 }} /> Download all ({pos.length})
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <div className="po-menu-scroll" style={{ maxHeight: 148, overflowY: "auto", overflowX: "auto" }}>
                    {pos.map((p) => (
                      <Dropdown.Item
                        key={p.id}
                        onClick={() => downloadOnePO(p).catch((e) => setDownloadErr(`PO download failed: ${e.message}`))}
                        title={poLabel(p)}
                      >
                        {poLabel(p)}
                      </Dropdown.Item>
                    ))}
                  </div>
                </>
              ) : (
                <Dropdown.Header>No PO documents on this quote.</Dropdown.Header>
              )}
            </Dropdown.Menu>
          </Dropdown>
          <Button className="qw-btn qw-btn-sm" style={{ background: "rgba(255,255,255,.18)", color: "#fff" }} onClick={() => navigate(backToQueue)}>
            <FaChevronLeft style={{ marginRight: 6 }} /> Back to Queue
          </Button>
        </div>
      </div>

      {downloadErr && (
        <Alert variant="danger" dismissible onClose={() => setDownloadErr(null)} style={{ fontSize: 13, marginBottom: 16 }}>
          Download failed: {downloadErr}
        </Alert>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 48, color: "#64748B" }}><Spinner animation="border" size="sm" /> Loading review…</div>
      ) : error ? (
        <Alert variant="danger" style={{ fontSize: 13 }}>{error}</Alert>
      ) : (
        <>
          {/* Snapshot + status */}
          <div className="qw-card-flat qw-pad" style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span className="qw-pill qw-pill-slate">{review.termsCode || review.planCategory}</span>
                  <Badge bg={systemCheckBg(liveSystemCheck)} text={liveSystemCheck === "Hold" ? "dark" : undefined}>{liveSystemCheck}</Badge>
                  <span style={{ fontSize: 12, fontWeight: 700, color: statusColor(meta.kind) }}>{meta.label}</span>
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A", marginTop: 6 }}>{review.customerName || review.custID || "—"}</div>
                {review.submittedBy && <div style={{ fontSize: 11.5, color: "#94A3B8" }}>Submitted by {review.submittedBy} {review.submittedAt ? `· ${fmtWhen(review.submittedAt)}` : ""}</div>}
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="qw-eyebrow">Grand total</div>
                <div className="qw-mono" style={{ fontSize: 22, fontWeight: 700, color: "#0052A5" }}>{fmtMoney(review.grandTotal)}</div>
                {review.orderNum && (
                  <Button variant="link" style={{ fontSize: 12, fontWeight: 700, color: "#10B981", marginTop: 2, padding: 0, textDecoration: "none" }}
                    onClick={() => navigate(`/order/${encodeURIComponent(review.orderNum)}`)}>
                    View Order #{review.orderNum} →
                  </Button>
                )}
              </div>
            </div>
            {(() => {
              // While In Review, derive the detail from current coverage + held count
              // so it stays accurate after a PO is deleted (G5); otherwise show the
              // stored snapshot detail.
              const detail = review.status === "PendingReview" && coverage
                ? [coverage.currentMessage, heldCount > 0 ? `⚠ ${heldCount} uploaded PO(s) not confirmed (held/needs review).` : ""].filter(Boolean).join(" ")
                : review.systemCheckDetail;
              return detail ? (
                <div style={{ fontSize: 12.5, color: "#475569", marginTop: 10, paddingTop: 10, borderTop: "1px dashed #E2E8F0" }}>{detail}</div>
              ) : null;
            })()}
            {review.status === "ChangesRequested" && review.returnReason && (
              <Alert variant="warning" style={{ fontSize: 12.5, marginTop: 10, marginBottom: 0 }}><strong>Returned:</strong> {review.returnReason}</Alert>
            )}
            {/* A PO delete clears the failure reason on the server (it described the old PO
                set), so no reason = stale: say so instead of showing a red banner. */}
            {review.status === "CreationFailed" && (review.creationError ? (
              <Alert variant="danger" style={{ fontSize: 12.5, marginTop: 10, marginBottom: 0 }}><strong>Creation failed:</strong> {review.creationError}</Alert>
            ) : (
              <Alert variant="info" style={{ fontSize: 12.5, marginTop: 10, marginBottom: 0 }}>The POs changed after the last order-creation attempt failed. Check the coverage, then run <strong>Create now</strong> again.</Alert>
            ))}
          </div>

          {/* Per-PO acknowledgements (once the order exists) */}
          {review.orderNum && <AckStatusPanel orderNum={review.orderNum} />}

          {/* Coverage tally */}
          {coverage && (
            <div className="qw-card-flat qw-pad" style={{ marginBottom: 14 }}>
              <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Coverage tally</div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A", whiteSpace: "nowrap" }}>
                  {coverage.unitCount > 0 ? `${coverage.unitsCovered ?? 0} of ${coverage.unitCount} ${coverage.unitLabel || "invoice"}s` : "PO match"}
                </span>
                <span className="qw-mono" style={{ fontSize: 12, color: "#64748B", whiteSpace: "nowrap" }}>
                  {fmtMoney(coverage.creditedAmount ?? coverage.totalConfirmedAmount)}&nbsp;of&nbsp;{fmtMoney(coverage.totalDue)}
                </span>
              </div>
              <div style={{ fontSize: 12.5, color: coverage.isMinimumMet ? "#166534" : "#92400E", display: "flex", gap: 6, alignItems: "flex-start" }}>
                {coverage.isMinimumMet ? <FaCheckCircle style={{ marginTop: 2, flexShrink: 0 }} /> : <FaExclamationTriangle style={{ marginTop: 2, flexShrink: 0 }} />}
                <span>{coverage.currentMessage}</span>
              </div>
            </div>
          )}

          {/* PO list */}
          <div className="qw-eyebrow" style={{ marginBottom: 8 }}>Purchase orders ({pos.length}{heldCount > 0 ? ` · ${heldCount} held` : ""})</div>
          <div className="qw-stack" style={{ marginBottom: 14 }}>
            {pos.length === 0 && <div style={{ fontSize: 13, color: "#94A3B8" }}>No POs uploaded.</div>}
            {pos.map((p) => {
              const held = p.confirmedPOAmount == null;
              return (
                <div key={p.id} className="qw-card-flat" style={{ padding: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <FaFilePdf style={{ color: "#B91C1C", flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.fileName}</div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 2 }}>
                        <span className="qw-mono" style={{ fontSize: 11.5, color: "#64748B" }}>{p.confirmedPONumber || p.extractedPONumber || "—"}</span>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{held ? <Badge bg="warning" text="dark">Held — needs review</Badge> : fmtMoney(p.confirmedPOAmount)}</span>
                        {p.extractionConfidence != null && (
                          <Badge bg={p.extractionConfidence >= 0.85 ? "success" : "warning"} text={p.extractionConfidence >= 0.85 ? undefined : "dark"}>
                            {Math.round(Number(p.extractionConfidence) * 100)}%
                          </Badge>
                        )}
                        {p.isDuplicate && (
                          <Badge bg="danger">{p.duplicateOfAttachmentId ? `Duplicate of #${p.duplicateOfAttachmentId}` : "Duplicate PO #"}</Badge>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                      <Button size="sm" variant="outline-secondary" title="Open PO PDF" onClick={() => viewPO(p.id)}><FaFilePdf /></Button>
                      {approver && hasAction("00042", true) && (
                        <Button size="sm" variant="outline-danger" title="Delete PO" disabled={busy === `del-${p.id}`} onClick={() => deletePO(p.id)}>
                          {busy === `del-${p.id}` ? <Spinner animation="border" size="sm" /> : <FaTrash />}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {actionErr && (
            <Alert variant={actionErr.code === "HELD_POS_PRESENT" ? "warning" : "danger"} style={{ fontSize: 13 }}>
              <strong>{actionErr.code || "Action failed"}.</strong> {actionErr.message}
            </Alert>
          )}

          {/* Decision bar — CS only, on a PendingReview quote */}
          {canDecide && (
            <div className="qw-card qw-pad">
              {heldCount > 0 && (
                <Form.Check
                  type="checkbox"
                  id="ack-held"
                  className="mb-2"
                  label={`I acknowledge ${heldCount} held PO(s) and approve anyway`}
                  checked={ackHeld}
                  onChange={(e) => setAckHeld(e.target.checked)}
                />
              )}
              {!returning ? (
                <div>
                  {/* Fail fast in the UI: coverage not met ⇒ can't approve/create
                      (the worker would re-check and bounce it anyway). Return stays
                      available so CS can send it back to the rep. */}
                  {coverageBlocks && (
                    <div style={{ fontSize: 12, color: "#B45309", marginBottom: 8 }}>
                      Coverage is not met (NotReady) — resolve PO coverage before approving or creating the order. You can still return it to the rep.
                    </div>
                  )}
                  {multiShip && (
                    <div style={{ fontSize: 12, color: "#1D4ED8", marginBottom: 8 }}>
                      Multi-ship plan — Approve approves the plan once. Each delivery then converts from the multi-ship page when its purchase order arrives; no order is created now.
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {/* Entitlements: 00045 approve, 00046 create-now, 00047 return */}
                    {hasAction("00045", true) && (
                    <Button variant="success" disabled={!!busy || coverageBlocks} title={coverageBlocks ? "Coverage not met — cannot approve" : undefined} onClick={approve}>
                      {busy === "approve" ? <><Spinner animation="border" size="sm" /> Approving…</> : <><FaCheckCircle style={{ marginRight: 6 }} /> {multiShip ? "Approve plan" : "Approve"}</>}
                    </Button>
                    )}
                    {hasAction("00046", true) && !multiShip && (
                    <Button variant="primary" disabled={!!busy || coverageBlocks} title={coverageBlocks ? "Coverage not met — cannot create order" : undefined} onClick={createNow}>
                      {busy === "createNow" ? <><Spinner animation="border" size="sm" /> Creating…</> : <><FaBolt style={{ marginRight: 6 }} /> Create now</>}
                    </Button>
                    )}
                    {hasAction("00047", true) && (
                    <Button variant="outline-warning" disabled={!!busy} onClick={() => { setReturning(true); setActionErr(null); }}>
                      <FaUndo style={{ marginRight: 6 }} /> Return to rep
                    </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <Form.Label className="small fw-semibold mb-1">Reason for returning (required)</Form.Label>
                  <Form.Control as="textarea" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. PO #2 references the wrong part — please re-upload." />
                  <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                    <Button variant="warning" disabled={busy === "return"} onClick={submitReturn}>
                      {busy === "return" ? <><Spinner animation="border" size="sm" /> Returning…</> : "Return to rep"}
                    </Button>
                    <Button variant="outline-secondary" disabled={busy === "return"} onClick={() => { setReturning(false); setReason(""); }}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {canRetryCreate && hasAction("00046", true) && (
            <div className="qw-card qw-pad">
              {heldCount > 0 && (
                <Form.Check
                  type="checkbox"
                  id="ack-held-retry"
                  className="mb-2"
                  label={`I acknowledge ${heldCount} held PO(s) and create the order anyway`}
                  checked={ackHeld}
                  onChange={(e) => setAckHeld(e.target.checked)}
                />
              )}
              {coverageBlocks && (
                <div style={{ fontSize: 12, color: "#B45309", marginBottom: 8 }}>
                  Coverage is not met (NotReady) — resolve PO coverage before creating the order.
                </div>
              )}
              <Button variant="primary" disabled={!!busy || coverageBlocks} title={coverageBlocks ? "Coverage not met — cannot create order" : undefined} onClick={createNow}>
                {busy === "createNow" ? <><Spinner animation="border" size="sm" /> Creating…</> : <><FaBolt style={{ marginRight: 6 }} /> Create now</>}
              </Button>
            </div>
          )}

          {approver && review?.status !== "PendingReview" && review?.status !== "OrderCreated" && !canRetryCreate && (
            <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 4 }}>
              Decision actions are available while the quote is <strong>In Review</strong>. Current status: {meta.label}.
            </div>
          )}

          {/* Audit / reviewer chain */}
          {Array.isArray(review.reviewerChain) && review.reviewerChain.length > 0 && (
            <div className="qw-card-flat qw-pad" style={{ marginTop: 14 }}>
              <div className="qw-eyebrow" style={{ marginBottom: 8 }}>History</div>
              {review.reviewerChain.map((s) => (
                <div key={s.step} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, padding: "2px 0" }}>
                  <span style={{ color: "#0F172A" }}>{s.actionLabel || s.action} <span style={{ color: "#94A3B8" }}>· {s.actor || "—"}</span></span>
                  <span className="qw-mono" style={{ color: "#94A3B8" }}>{fmtWhen(s.at)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default QuoteReviewDetail;
